import { useCallback } from 'react'
import { Address } from 'viem'
import { useAccount } from 'wagmi'

import stakeNftMeyAbi from 'abi/abi-staking-nft-mey.json'
import { ContractStatusEnum } from 'constants/index'
import { DECIMALS_WEI_ETH } from 'constants/number'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useContractManager } from 'hooks/use-contract-manager'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { parserPercentContract, parserUnitContract } from 'utils/contract'
import { logInfo } from 'utils/logger'

interface Params {
  fields: {
    startDate: number
    endDate: number
    isActive: boolean
    rewardBase: number
    duration: number
    aprBonusTiers: { tierId: string; aprBonusBP: number }[]
  }
  ptoId: string
  isUpdate?: boolean
  collectionAddress: string
}

export const useStakeNFTMeyReward = (contractAddress: Address | null) => {
  const { address } = useAccount()
  const contract = useContractViem(contractAddress, stakeNftMeyAbi)
  const { isContractManager } = useContractManager({ contract })

  const prepareMeyContractParams = ({
    fields,
    ptoId,
    collectionAddress,
    isUpdate = false,
  }: Params) => {
    const formattedRewardBonusTiers = fields.aprBonusTiers.map((t: any) => ({
      tierId: BigInt(t.tierId),
      rewardBonus: parserPercentContract(t.aprBonusBP),
    }))

    return [
      collectionAddress,
      ptoId,
      parserUnitContract(fields.rewardBase, DECIMALS_WEI_ETH),
      isUpdate ? fields.isActive : undefined,
      BigInt(fields.startDate),
      BigInt(fields.endDate),
      BigInt(fields.duration),
      formattedRewardBonusTiers,
    ].filter((v) => v != undefined)
  }

  const addNftToStakeMeyPool = useCallback(
    async (params: Params) => {
      try {
        const contractParams = prepareMeyContractParams(params)

        const { request } = await contract.simulate.addNFTCollection(contractParams, {
          account: address,
        })

        const hash = await clientWallet.writeContract(request)

        const receipt = await publicWallet.waitForTransactionReceipt({
          hash,
          confirmations: 1,
        })

        if (receipt.status === ContractStatusEnum.SUCCESS) {
          logInfo('Deployed pool address')
          return receipt
        }

        return null
      } catch (error) {
        console.error('Error adding NFT to stake pool:', error)
        throw error
      }
    },
    [contract, address]
  )

  const updateNftToStakeMeyPool = useCallback(
    async (params: Params) => {
      try {
        const contractParams = prepareMeyContractParams({ ...params, isUpdate: true })
        console.log(contractParams)

        const { request } = await contract.simulate.updateNFTCollection(contractParams, {
          account: address,
        })

        const hash = await clientWallet.writeContract(request)

        const receipt = await publicWallet.waitForTransactionReceipt({
          hash,
          confirmations: 1,
        })

        if (receipt.status === ContractStatusEnum.SUCCESS) {
          logInfo('Deployed pool address')
          return receipt
        }

        return null
      } catch (error) {
        console.error('Error edit NFT to stake pool:', error)
        throw error
      }
    },
    [contract, address]
  )

  return { isContractManager, addNftToStakeMeyPool, updateNftToStakeMeyPool }
}
